import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Scale, Download, Users2, UserX, GitBranch, Loader2, ArrowUpDown, TrendingUp, TrendingDown } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { minDelay } from "@/lib/ux";
import { Loading, ErrorView, EmptyState } from "@/components/StateViews";
import { useAuth } from "@/contexts/AuthContext";
import { initials } from "@/lib/format";
import DateRangePicker from "@/components/DateRangePicker";
import { toast } from "sonner";

const COLUMNS = [
  { key: "name", label: "Salesperson", align: "left" },
  { key: "assigned", label: "Assigned" },
  { key: "share_pct", label: "Share %", suffix: "%" },
  { key: "deviation", label: "vs Fair" },
  { key: "by_rule", label: "By Rule" },
  { key: "by_round_robin", label: "Round-Robin" },
  { key: "manual", label: "Manual" },
  { key: "ace_king_pct", label: "Ace+King %", suffix: "%" },
  { key: "won", label: "Won" },
  { key: "conversion", label: "Conv %", suffix: "%", accent: true },
  { key: "sla_breaches", label: "SLA Breach" },
];

const FAIRNESS_TONE = {
  Balanced: { text: "text-emerald-600", bg: "bg-emerald-50", ring: "ring-emerald-200", bar: "bg-emerald-500" },
  "Slightly imbalanced": { text: "text-amber-600", bg: "bg-amber-50", ring: "ring-amber-200", bar: "bg-amber-500" },
  Imbalanced: { text: "text-red-600", bg: "bg-red-50", ring: "ring-red-200", bar: "bg-red-500" },
};

export default function AssignmentReport() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [filters, setFilters] = useState({ created_from: "", created_to: "" });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [sort, setSort] = useState({ key: "assigned", dir: "desc" });
  const [exporting, setExporting] = useState(false);

  const isAdmin = user?.role === "Admin";

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
      const { data } = await minDelay(api.get("/assignment-report", { params }));
      setData(data.data);
    } catch (e) { setError(apiError(e)); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const toggleSort = (key) => setSort((s) => ({ key, dir: s.key === key && s.dir === "desc" ? "asc" : "desc" }));

  // Dynamic Fairness/Assignment engine (Phase 2b) — team-level, behind a toggle.
  const [dynOn, setDynOn] = useState(false);
  const [dyn, setDyn] = useState(null);
  const [dynCfg, setDynCfg] = useState(null);
  useEffect(() => {
    if (!dynOn) return;
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    api.get("/scoring/compute-batch", { params: { entity_type: "fairness", ...params } })
      .then(({ data }) => { setDynCfg(data.data.config); setDyn(data.data.rows?.[0] || null); })
      .catch(() => setDyn(null));
  }, [dynOn, filters]); // eslint-disable-line

  const sortedRows = data?.rows ? [...data.rows].sort((a, b) => {
    const x = a[sort.key], y = b[sort.key];
    const cmp = sort.key === "name" ? String(x).localeCompare(String(y)) : ((Number(x) || 0) - (Number(y) || 0));
    return sort.dir === "desc" ? -cmp : cmp;
  }) : [];

  const doExport = async () => {
    setExporting(true);
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
      const res = await api.get("/assignment-report/export", { params, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = "assignment_report.csv";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Assignment report exported");
    } catch (e) { toast.error(apiError(e)); }
    finally { setExporting(false); }
  };

  if (loading && !data) return <Loading label="Loading assignment report..." />;
  if (error) return <ErrorView message={error} onRetry={load} />;

  const s = data.summary;
  const tone = FAIRNESS_TONE[s.fairness_label] || FAIRNESS_TONE.Balanced;
  const maxAssigned = Math.max(1, ...sortedRows.map((r) => r.assigned));
  const mt = s.method_totals;
  const methodTotal = Math.max(1, mt.by_rule + mt.by_round_robin + mt.manual);

  return (
    <div className="space-y-6" data-testid="assignment-report-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 font-sora text-3xl font-bold text-navy"><Scale className="h-7 w-7 text-loop" />Fairness Report</h1>
          <p className="flex items-center gap-2 text-sm text-ink/60">
            How leads are distributed across the team — load balance, routing method & accountability.
            {loading && data && <span data-testid="ar-refreshing" className="flex items-center gap-1 text-loop"><Loader2 className="h-3.5 w-3.5 animate-spin" />Refreshing…</span>}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <DateRangePicker
            from={filters.created_from}
            to={filters.created_to}
            onChange={({ created_from, created_to }) => setFilters((f) => ({ ...f, created_from, created_to }))}
            testid="ar-date-range"
          />
          {isAdmin && (
            <button onClick={doExport} disabled={exporting} data-testid="ar-export-btn" className="flex h-9 items-center gap-1.5 rounded-lg border border-sky px-3 text-sm font-medium text-navy hover:border-loop hover:text-loop disabled:opacity-60">
              {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}Export
            </button>
          )}
          <button onClick={() => setDynOn((v) => !v)} data-testid="ar-dyn-toggle" title={dynCfg ? `${dynCfg.name} v${dynCfg.version}` : "Use the Admin-configured dynamic fairness engine"}
            className={`h-9 rounded-lg px-3 text-sm font-semibold transition-colors ${dynOn ? "bg-loop text-white" : "border border-loop/40 text-loop hover:bg-loop/5"}`}>
            {dynOn ? `Dynamic engine: ON${dynCfg ? ` · v${dynCfg.version}` : ""}` : "Dynamic engine (beta)"}
          </button>
        </div>
      </div>

      {dynOn && dyn && (
        <div data-testid="ar-dyn-card" className="rounded-2xl border border-sky/70 bg-white p-5">
          <div className="mb-3 flex flex-wrap items-center gap-3">
            <h3 className="font-sora text-sm font-semibold text-navy">{dynCfg?.name} <span className="text-ink/40">v{dynCfg?.version}</span></h3>
            <span className={`inline-flex items-center gap-2 rounded-xl px-3 py-1.5 ${dyn.score >= 85 ? "bg-emerald-100 text-emerald-700" : dyn.score >= 70 ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}`} data-testid="ar-dyn-score">
              <span className="font-sora text-2xl font-bold">{dyn.insufficient || dyn.score == null ? "—" : dyn.score}</span>
              <span className="text-sm font-semibold">{dyn.label}</span>
            </span>
          </div>
          <div className="grid gap-2 md:grid-cols-2">
            {(dyn.breakdown || []).map((b) => (
              <div key={b.key} className="flex items-center gap-2 rounded-lg border border-sky/50 bg-mist/20 px-3 py-2 text-sm">
                <span className="font-medium text-navy">{b.label}</span>
                <span className="text-[11px] text-ink/40">{b.weight}%</span>
                <div className="ml-auto flex items-center gap-2">
                  {b.available ? (<><div className="h-1.5 w-20 overflow-hidden rounded-full bg-ink/10"><div className="h-full rounded-full bg-loop" style={{ width: `${b.score}%` }} /></div><span className="w-9 text-right font-semibold text-navy">{b.score}</span></>) : <span className="text-[11px] italic text-ink/40">no data</span>}
                </div>
              </div>
            ))}
          </div>
          {(dyn.flags || []).length > 0 && (
            <div className="mt-3 space-y-1.5">
              {dyn.flags.map((f) => (
                <div key={f.key} className={`rounded-lg px-3 py-2 ${f.severity === "critical" ? "bg-red-50 text-red-700" : f.severity === "warning" ? "bg-amber-50 text-amber-700" : f.severity === "positive" ? "bg-emerald-50 text-emerald-700" : "bg-sky-50 text-sky-700"}`}>
                  <span className="text-sm font-semibold">{f.label}</span>
                  {f.diagnosis && <p className="text-xs opacity-80">{f.diagnosis}</p>}
                  {f.recommended_action && <p className="text-xs">→ {f.recommended_action}</p>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Fairness hero + summary */}
      <div className="grid gap-3 lg:grid-cols-4">
        <div data-testid="ar-fairness-card" className={`rounded-2xl border border-sky/70 bg-white p-5 ring-1 ${tone.ring}`}>
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold uppercase tracking-wide text-ink/50">Fairness Score</p>
            <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${tone.bg} ${tone.text}`}>{s.fairness_label}</span>
          </div>
          <p data-testid="ar-fairness-score" className={`mt-2 font-sora text-4xl font-bold ${tone.text}`}>{s.fairness_score}<span className="text-lg text-ink/40">/100</span></p>
          <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-mist">
            <div className={`h-full rounded-full ${tone.bar} transition-all`} style={{ width: `${s.fairness_score}%` }} />
          </div>
          <p className="mt-2 text-[11px] text-ink/50">Across {s.active_execs} active rep{s.active_execs === 1 ? "" : "s"} · expected ~{s.expected_per_rep}/rep</p>
        </div>

        <div className="grid grid-cols-2 gap-3 lg:col-span-3 lg:grid-cols-3">
          {[
            { label: "Total Assigned", value: s.total_assigned, icon: Users2, tone: "text-loop", bg: "bg-sky/50", testid: "ar-total-assigned" },
            { label: "Unassigned", value: s.unassigned, icon: UserX, tone: s.unassigned > 0 ? "text-amber-600" : "text-ink/60", bg: "bg-amber-50", testid: "ar-unassigned", onClick: () => navigate("/leads?assigned=false") },
            { label: "Active Reps", value: s.active_execs, icon: GitBranch, tone: "text-navy", bg: "bg-mist", testid: "ar-active-reps" },
            { label: "Most Loaded", value: s.most_loaded ? s.most_loaded.name : "—", sub: s.most_loaded ? `${s.most_loaded.assigned} leads` : "", icon: TrendingUp, tone: "text-red-500", bg: "bg-red-50", testid: "ar-most-loaded" },
            { label: "Least Loaded", value: s.least_loaded ? s.least_loaded.name : "—", sub: s.least_loaded ? `${s.least_loaded.assigned} leads` : "", icon: TrendingDown, tone: "text-emerald-600", bg: "bg-emerald-50", testid: "ar-least-loaded" },
            { label: "Routing Mix", value: `${Math.round(mt.by_rule / methodTotal * 100)}% rule`, sub: `${Math.round(mt.by_round_robin / methodTotal * 100)}% RR · ${Math.round(mt.manual / methodTotal * 100)}% manual`, icon: Scale, tone: "text-violet-600", bg: "bg-violet-50", testid: "ar-routing-mix" },
          ].map((t) => (
            <div key={t.label} data-testid={t.testid} onClick={t.onClick} className={`rounded-2xl border border-sky/70 bg-white p-4 ${t.onClick ? "cursor-pointer hover:border-loop" : ""}`}>
              <div className={`mb-3 flex h-9 w-9 items-center justify-center rounded-xl ${t.bg} ${t.tone}`}><t.icon className="h-[18px] w-[18px]" /></div>
              <p className="truncate font-sora text-xl font-bold text-navy">{t.value}</p>
              <p className="truncate text-xs text-ink/60">{t.label}{t.sub ? ` · ${t.sub}` : ""}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Per-rep table */}
      {!sortedRows.length ? (
        <EmptyState title="No assignments in this range" subtitle="Try a wider date range." />
      ) : (
        <div className="overflow-hidden rounded-2xl border border-sky/70 bg-white">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-mist text-left text-xs font-semibold uppercase tracking-wide text-ink/50">
                <tr>
                  {COLUMNS.map((col) => (
                    <th key={col.key} onClick={() => toggleSort(col.key)} data-testid={`ar-sort-${col.key}`} className={`cursor-pointer whitespace-nowrap px-4 py-3 hover:text-loop ${col.align === "left" ? "text-left" : "text-right"}`}>
                      <span className={`inline-flex items-center gap-1 ${col.align === "left" ? "" : "justify-end"}`}>
                        {col.label}<ArrowUpDown className={`h-3 w-3 ${sort.key === col.key ? "text-loop" : "text-ink/30"}`} />
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-sky/50">
                {sortedRows.map((r) => (
                  <tr key={r.user_id} data-testid={`ar-row-${r.user_id}`} onClick={() => navigate(`/leads?owner_user_id=${r.user_id}`)} className="cursor-pointer transition-colors hover:bg-sky/20">
                    <td className="whitespace-nowrap px-4 py-3">
                      <div className="flex items-center gap-2.5">
                        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-navy text-[11px] font-bold text-white">{initials(r.name)}</span>
                        <div className="min-w-0">
                          <p className="truncate font-medium text-navy">{r.name}</p>
                          <p className="text-[11px] text-ink/45">{r.role}{r.status !== "Active" ? ` · ${r.status}` : ""}{!r.in_pool ? " · not in pool" : ""}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <div className="hidden h-2 w-16 overflow-hidden rounded-full bg-mist sm:block">
                          <div className="h-full rounded-full bg-loop" style={{ width: `${r.assigned / maxAssigned * 100}%` }} />
                        </div>
                        <span className="font-semibold text-navy">{r.assigned}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right text-ink/80">{r.share_pct}%</td>
                    <td className="px-4 py-3 text-right">
                      {r.deviation === null ? <span className="text-ink/30">—</span> : (
                        <span className={`font-medium ${r.deviation > 0.5 ? "text-red-500" : r.deviation < -0.5 ? "text-amber-600" : "text-emerald-600"}`}>
                          {r.deviation > 0 ? "+" : ""}{r.deviation}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right text-ink/80">{r.by_rule}</td>
                    <td className="px-4 py-3 text-right text-ink/80">{r.by_round_robin}</td>
                    <td className="px-4 py-3 text-right text-ink/80">{r.manual}</td>
                    <td className="px-4 py-3 text-right text-ink/80">{r.ace_king_pct}%</td>
                    <td className="px-4 py-3 text-right text-ink/80">{r.won}</td>
                    <td className="px-4 py-3 text-right font-semibold text-emerald-600">{r.conversion}%</td>
                    <td className="px-4 py-3 text-right">
                      {r.sla_breaches > 0 ? <span className="font-medium text-red-500">{r.sla_breaches}</span> : <span className="text-ink/40">0</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
